import React from "react";
import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { Stack } from "@chakra-ui/layout";
import { Button, Input, Modal, useTranslation } from "@familyzone/component-library";
import CriteriaSelector, { CriteriaTypes } from "../../modules/criteria/CriteriaSelector";
import { FilteringSourceCriteria, Fingerprint, IpObject, MacObject } from "../../modules/criteria/criteriaTypes";
import { getFingerprints, getObjects, getTimePeriods } from "../../pages/filtering/ApiHelpers";
import CardSpinner from "../reporting/UserDashboard/CardSpinner";
import { TimePeriod } from "../../pages/filtering/types";

interface ManyToOneNatRule {
  comment: string;
  natTargetIp?: string;
  source_criteria?: FilteringSourceCriteria[];
  destination_criteria?: FilteringSourceCriteria[];
}

interface IManyToOneNatModalProps {
  handleHide: () => void;
  isOpen: boolean;
  saveSelection: (rule: unknown) => void;
  selectedSourceCriteria: FilteringSourceCriteria[];
  selectedDestinationCriteria: FilteringSourceCriteria[];
  updateParentSourceState: (criteria: FilteringSourceCriteria[]) => void;
  updateParentDestinationState: (criteria: FilteringSourceCriteria[]) => void;
  changeRuleState: (rule: ManyToOneNatRule) => void;
  rule: ManyToOneNatRule;
}

const ManyToOneNatModal: React.FC<IManyToOneNatModalProps> = ({
  handleHide,
  isOpen,
  saveSelection,
  selectedSourceCriteria,
  selectedDestinationCriteria,
  updateParentDestinationState,
  updateParentSourceState,
  changeRuleState,
  rule,
}) => {
  const { t } = useTranslation();
  const [sourceSelection, setSourceSelection] = React.useState<FilteringSourceCriteria[]>(selectedSourceCriteria);
  const [destinationSelection, setDestinationSelection] = React.useState<FilteringSourceCriteria[]>(selectedDestinationCriteria);
  const [ipObjects, setIpObjects] = React.useState<IpObject[]>([]);
  const [macObjects, setMacObjects] = React.useState<MacObject[]>([]);
  const [fingerprints, setFingerprints] = React.useState<Fingerprint[]>([]);
  const [timePeriods, setTimePeriods] = React.useState<TimePeriod[]>([]);
  const [loading, setLoading] = React.useState(true);

  const sourceOptions: Partial<CriteriaTypes> = {
    "source.user": "User",
    protocol: "Protocol",
    "ipv4.range": "Network Range",
    ipv4: "Network",
    "ipv4.address": "IP Address",
    "ipv4.alias": "IP Address Object",
    "source.mac": "Mac Address",
    fingerprint: "Device Type",
    "source.mac.pool": "Mac Address Object",
    "transport.port": "Port",
    "transport.portrange": "Port Range",
    device: "Interface",
    timeperiod: "Time Periods",
  };

  const destinationOptions: Partial<CriteriaTypes> = {
    "ipv4.range": "Network Range",
    ipv4: "Network",
    "ipv4.address": "IP Address",
    "ipv4.alias": "IP Address Object",
    "transport.port": "Port",
    "transport.portrange": "Port Range",
    device: "Interface",
    geoip: "Country",
  };

  const handleChangeSource = (criteria: FilteringSourceCriteria[]) => {
    updateParentSourceState(criteria);
    setSourceSelection(criteria);
  };

  const handleChangeDestination = (criteria: FilteringSourceCriteria[]) => {
    updateParentDestinationState(criteria);
    setDestinationSelection(criteria);
  };

  const handleSave = () => {
    saveSelection(rule);
  };

  const setObjectsCallback = React.useCallback(async () => {
    const result = await getObjects();
    setFingerprints(await getFingerprints());
    setTimePeriods(await getTimePeriods());
    const ipObjects = result.filter((obj): obj is IpObject => obj.type === 1 || obj.type === 0);
    const macObjects = result.filter((obj): obj is MacObject => obj.type === 4);
    setIpObjects(ipObjects);
    setMacObjects(macObjects);
  }, []);

  React.useEffect(() => {
    void setObjectsCallback();
    setLoading(false);
  }, [setObjectsCallback]);

  return (
    <Modal
      isOpen={isOpen}
      size="md"
      onClose={() => handleHide()}
      headerText={t("Edit Rule")}
      contentProps={{ style: { overflow: "scroll" } }}
    >
      <>
        {loading && <CardSpinner />}
        {!loading && (
          <Stack spacing="sp12" mt="sp8">
            <FormControl>
              <FormLabel>{t("Name")}</FormLabel>
              <Input value={rule.comment} onChange={(e) => changeRuleState({ ...rule, comment: e.target.value })} placeholder="Rule Name" />
            </FormControl>
            <FormControl>
              <FormLabel>{t("Source")}</FormLabel>
              <CriteriaSelector
                ipObjects={ipObjects}
                macObjects={macObjects}
                fingerprints={fingerprints}
                customOptions={sourceOptions}
                timePeriods={timePeriods}
                selected={sourceSelection}
                onChange={(criteria) => handleChangeSource(criteria)}
                errors={[]}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{t("Destination")}</FormLabel>
              <CriteriaSelector
                ipObjects={ipObjects}
                customOptions={destinationOptions}
                selected={destinationSelection}
                onChange={(criteria) => handleChangeDestination(criteria)}
                errors={[]}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{t("Masquerade IP")}</FormLabel>
              <Input
                value={rule.natTargetIp}
                onChange={(e) => changeRuleState({ ...rule, natTargetIp: e.target.value })}
                placeholder="Enter a masquerade IP"
              />
            </FormControl>
            <FormControl>
              <Button variant="primary" onClick={handleSave}>
                Save
              </Button>
            </FormControl>
          </Stack>
        )}
      </>
    </Modal>
  );
};

export default ManyToOneNatModal;
